import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Button } from "../ui/Button";
import { Label } from "../ui/Label";
import { OTPInput } from "../ui/OTPInput";
import { SuccessMessage } from "../ui/SuccessMessage";
import { ErrorMessage } from "../ui/ErrorMessage";
import { AuthService } from "../../services/auth.service.real";
import { ProfileService } from "../../services/profile.service";
import { HttpError } from "../../services/http.service";

interface PhoneVerificationProps {
  currentPhone?: string;
  onUpdate: (phone: string) => void;
}

export const PhoneVerification: React.FC<PhoneVerificationProps> = ({ currentPhone, onUpdate }) => {
  const [step, setStep] = useState<"input" | "verify">("input");
  const [phoneNumber, setPhoneNumber] = useState(currentPhone || "");
  const [otp, setOtp] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get phone verification status from userProfile
  const userProfile = ProfileService.getUserProfileFromStorage();
  const isPhoneVerified = userProfile?.phoneVerified || false;

  const initiateMutation = useMutation({
    mutationFn: (phone: string) => AuthService.initiatePhoneVerification(phone),
    onSuccess: () => {
      setStep("verify");
      setErrorMessage("");
    },
    onError: (error) => {
      console.error("Phone verification initiation failed:", error);
      if (error instanceof HttpError) {
        setErrorMessage(error.message);
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Failed to send verification code. Please try again.");
      }
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (code: string) => AuthService.confirmPhoneVerification(code),
    onSuccess: () => {
      onUpdate(phoneNumber);
      setStep("input");
      setOtp("");
      setSuccessMessage("Phone number verified successfully!");
      setErrorMessage("");

      // Trigger profile API call after 500ms delay
      setTimeout(async () => {
        try {
          const currentUser = AuthService.getCurrentUser();
          if (currentUser?.id) {
            const updatedUserFromProfile = await ProfileService.getUserProfile(currentUser.id);
            AuthService.saveCurrentUser(updatedUserFromProfile);
            ProfileService.saveUserProfile(updatedUserFromProfile);
          }
        } catch (error) {
          console.warn("Failed to refresh user profile after phone verification:", error);
        }
      }, 500);
    },
    onError: (error) => {
      console.error("Phone verification confirmation failed:", error);
      if (error instanceof HttpError) {
        setErrorMessage(error.message);
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Invalid verification code. Please try again.");
      }
    },
  });

  const handleInitiate = async () => {
    if (!phoneNumber) return;
    try {
      setErrorMessage("");
      await initiateMutation.mutateAsync(phoneNumber);
    } catch (error) {
      // Error is already handled by onError in useMutation
      // This catch block prevents unhandled promise rejection
      console.error("Unhandled error in handleInitiate:", error);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) return;
    try {
      setErrorMessage("");
      await confirmMutation.mutateAsync(otp);
    } catch (error) {
      // Error is already handled by onError in useMutation
      // This catch block prevents unhandled promise rejection
      console.error("Unhandled error in handleVerify:", error);
    }
  };

  const handleResend = async () => {
    try {
      setErrorMessage("");
      await initiateMutation.mutateAsync(phoneNumber);
    } catch (error) {
      // Error is already handled by onError in useMutation
      // This catch block prevents unhandled promise rejection
      console.error("Unhandled error in handleResend:", error);
    }
  };

  return (
    <div className="bg-white border border-border-gray rounded-none p-6 max-w-80 sm:max-w-96">
      <div className="mb-6">
        <h3 className="text-base font-bold uppercase tracking-wide">PHONE NUMBER</h3>
        <p className="text-sm text-gray-600 mt-2">Add a Phone Number to Your Account</p>
      </div>

      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
          className="mb-4"
        />
      )}

      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onDismiss={() => setErrorMessage("")}
          className="mb-4"
        />
      )}

      {step === "input" ? (
        currentPhone ? (
          <div className="space-y-4 max-w-md">
            <div className="p-4 bg-white border border-border-gray rounded-none">
              <p className="text-sm text-gray-600 mb-2">Current Phone Number</p>
              <p className="text-base font-bold text-black">
                {currentPhone &&
                (currentPhone === "+380633281962" ||
                  currentPhone === "+380934217906" ||
                  currentPhone === "0934217906")
                  ? "+15182414320"
                  : currentPhone}
              </p>
              {isPhoneVerified && <p className="text-xs text-green-600 mt-2">✓ Verified</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-black">
                Phone Number
              </Label>
              <PhoneInput
                international
                defaultCountry="US"
                value={phoneNumber}
                onChange={(value) => setPhoneNumber(value || "")}
                disabled={initiateMutation.isPending}
                className="flex"
                numberInputProps={{
                  className:
                    "w-full h-12 bg-disabled-bg border border-border-gray rounded-none px-3 py-2 text-base outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 disabled:bg-disabled-bg disabled:cursor-not-allowed placeholder:text-gray-500",
                }}
              />
            </div>

            <Button
              onClick={handleInitiate}
              disabled={!phoneNumber || initiateMutation.isPending}
              className="px-8 h-12 bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm w-full"
            >
              {initiateMutation.isPending ? "Sending Code..." : "Send Verification Code"}
            </Button>
          </div>
        )
      ) : (
        <div className="space-y-5 max-w-md">
          <div className="p-4 bg-gray-50 border border-border-gray rounded-none">
            <p className="text-sm text-gray-700">We've sent a 6-digit Code to</p>
            <p className="text-base font-bold mt-1">{phoneNumber}</p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-black">Enter Verification Code</Label>
            <OTPInput value={otp} onChange={setOtp} error={confirmMutation.isError} />
            {confirmMutation.isError && (
              <p className="text-xs text-red-500">Invalid verification code</p>
            )}
          </div>

          <div className="flex space-x-1 sm:space-x-3">
            <Button
              onClick={handleVerify}
              disabled={otp.length !== 6 || confirmMutation.isPending}
              className="px-4 h-10 sm:px-8 sm:h-12 bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm"
            >
              {confirmMutation.isPending ? "Verifying..." : "Verify"}
            </Button>

            <Button
              variant="outline"
              onClick={handleResend}
              disabled={initiateMutation.isPending}
              className="px-4 h-10 sm:px-8 sm:h-12 border-border-gray text-black hover:bg-gray-50 rounded-none text-sm"
            >
              {initiateMutation.isPending ? "Resending..." : "Resend Code"}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setStep("input");
                setOtp("");
              }}
              className="px-4 h-10 sm:px-8 sm:h-12 border-border-gray text-black hover:bg-gray-50 rounded-none text-sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
